import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Empty, Spin, message } from 'antd';
import { debounce } from 'lodash';
import InfiniteScroll from 'react-infinite-scroller';
import { useTranslation } from 'react-i18next';

import BookingOrderAPI, {
  BookingOrderApiFilter,
  IncompleteBookingOrderApiFilter,
} from '../api/BookingOrderAPI';
import BookingOrder, { BookingOrderStatus } from '../types/BookingOrder';
import { useUser } from '../../user/userContext';
import BookingOrderCard from '../components/BookingOrderCard';
import PaginationParams from '../../types/PaginationParams';
import PaginationMeta from '../../types/PaginationMeta';
import BookingOrderFilterPicker from '../components/BookingOrderFilterPicker';
import ApiResponse from '../../types/ApiResponse';
import { Permission } from '../../user/types/Permission';
import { userHasPermission } from '../../user/userUtils';
import { useSearchParams } from 'react-router-dom';
import BookingOrderFilterTabs from '../components/BookingOrderFilterTabs';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { AxiosResponse } from 'axios';
import { getBookingOrderFilterFromSearchParams } from '../utils/bookingOrder';
import {
  getNumberListFromURLSearchParams,
  setNumberListInURLSearchParams,
} from '../../utils/searchParams';
import { useFeatureToggles } from '../../context/featureToggles';
import OpenClassAPI from '../../openClasses/OpenClassAPI';
import { OpenClassBookingOrderSummary } from '../../openClasses/types/OpenClass';
import OpenClassBookingOrderResults from '../components/OpenClassBookingOrderResults';
import OpenClassBookingOrderFilterPicker from '../components/OpenClassBookingOrderFilterPicker';

export enum BookingOrderFilterCategory {
  All = 'all',
  Urgent = 'urgent',
  Ordered = 'ordered',
  Preliminary = 'preliminary',
  Confirmed = 'confirmed',
  Canceled = 'canceled',
  OpenClassOrders = 'openClassOrders',
}

export enum SearchParamKeys {
  SubsidiaryCompanyIds = 'subsidiaryCompanyIds',
  ClientCompanyIds = 'clientCompanyIds',
  CourseIds = 'courseIds',
  From = 'from',
  To = 'to',
  InstructorIds = 'instructorIds',
  ContactPersonIds = 'contactPersonIds',
  ResponsibleBookerIds = 'responsibleBookerIds',
  InvoiceRecipientIds = 'invoiceRecipientIds',
  IsInvoiced = 'isInvoiced',
  Statuses = 'statuses',
  Category = 'category',
}

dayjs.extend(customParseFormat);

const asCalendarDate = (value?: string): string | undefined => {
  if (!value) return undefined;
  const parsed = dayjs(value, 'YYYY-MM-DD', true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
};

const BookingOrders: React.FC = () => {
  const [currentUser] = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookingOrders, setBookingOrders] = useState<BookingOrder[]>([]);
  const [openClassBookingOrders, setOpenClassBookingOrders] = useState<
    OpenClassBookingOrderSummary[]
  >([]);
  const [loadingBookingOrders, setLoadingBookingOrders] =
    useState<boolean>(true);
  const [loadingOpenClassBookingOrders, setLoadingOpenClassBookingOrders] =
    useState(false);
  const [openClassPaginationParams, setOpenClassPaginationParams] =
    useState<PaginationParams>({ page: 1 });
  const [openClassPaginationMeta, setOpenClassPaginationMeta] =
    useState<PaginationMeta | null>();
  const [paginationParams, setPaginationParams] =
    useState<PaginationParams | null>({
      page: 1,
    });
  const [paginationMeta, setPaginationMeta] = useState<PaginationMeta | null>();
  const [cachedFilterValues, setCachedFilterValues] =
    useState<BookingOrderApiFilter>({});
  const [currentTab, setCurrentTab] = useState<BookingOrderFilterCategory>(
    (searchParams.get('category') as BookingOrderFilterCategory) ??
      BookingOrderFilterCategory.Ordered,
  );

  const { t } = useTranslation();
  const { isOpenClassesEnabled } = useFeatureToggles();
  const isOpenClassOrdersTab =
    isOpenClassesEnabled &&
    currentTab === BookingOrderFilterCategory.OpenClassOrders;

  const bookingOrderApiFilter = useMemo(() => {
    const from = searchParams.get(SearchParamKeys.From);
    const to = searchParams.get(SearchParamKeys.To);
    const subsidiaryCompanyIds = searchParams
      .getAll(SearchParamKeys.SubsidiaryCompanyIds)
      ?.map(Number);
    const clientCompanyIds = searchParams
      .getAll(SearchParamKeys.ClientCompanyIds)
      ?.map(Number);
    const courseIds = searchParams
      .getAll(SearchParamKeys.CourseIds)
      ?.map(Number);
    const instructorIds = searchParams
      .getAll(SearchParamKeys.InstructorIds)
      ?.map((id) => (id === 'null' ? null : Number(id)));
    const responsibleBookerIds = searchParams
      .getAll(SearchParamKeys.ResponsibleBookerIds)
      ?.map((id) => (id === 'null' ? null : Number(id)));
    const invoiceRecipientIds = searchParams
      .getAll(SearchParamKeys.InvoiceRecipientIds)
      ?.map((id) => (id === 'null' ? null : Number(id)));
    const isInvoicedParam = searchParams.get(SearchParamKeys.IsInvoiced);
    const isInvoiced =
      isInvoicedParam === 'true'
        ? true
        : isInvoicedParam === 'false'
          ? false
          : undefined;
    const statuses = searchParams.getAll(
      SearchParamKeys.Statuses,
    ) as BookingOrderStatus[];

    return {
      from: from ?? undefined,
      to: to ?? undefined,
      subsidiaryCompanyIds,
      clientCompanyIds,
      courseIds,
      instructorIds,
      responsibleBookerIds,
      invoiceRecipientIds,
      isInvoiced,
      statuses: statuses.length > 0 ? statuses : undefined,
    } as BookingOrderApiFilter;
  }, [searchParams]);

  const incompleteBookingOrderApiFilter: IncompleteBookingOrderApiFilter =
    useMemo(() => {
      return {
        from: bookingOrderApiFilter.from ?? undefined,
        to: bookingOrderApiFilter.to ?? undefined,
        subsidiaryCompanyIds: bookingOrderApiFilter.subsidiaryCompanyIds,
        responsibleBookerIds: bookingOrderApiFilter.responsibleBookerIds,
        isInvoiced: bookingOrderApiFilter.isInvoiced,
      };
    }, [bookingOrderApiFilter]);

  const setSearchParamsToCachedValues = (urlSearchParams: URLSearchParams) => {
    if (cachedFilterValues.from) {
      urlSearchParams.set(SearchParamKeys.From, cachedFilterValues.from);
    } else {
      urlSearchParams.delete(SearchParamKeys.From);
    }

    if (cachedFilterValues.to) {
      urlSearchParams.set(SearchParamKeys.To, cachedFilterValues.to);
    } else {
      urlSearchParams.delete(SearchParamKeys.To);
    }

    setNumberListInURLSearchParams(
      urlSearchParams,
      SearchParamKeys.ClientCompanyIds,
      cachedFilterValues.clientCompanyIds,
    );
    setNumberListInURLSearchParams(
      urlSearchParams,
      SearchParamKeys.InstructorIds,
      cachedFilterValues.instructorIds,
    );
    setNumberListInURLSearchParams(
      urlSearchParams,
      SearchParamKeys.CourseIds,
      cachedFilterValues.courseIds,
    );
    setNumberListInURLSearchParams(
      urlSearchParams,
      SearchParamKeys.InvoiceRecipientIds,
      cachedFilterValues.invoiceRecipientIds,
    );
    if (cachedFilterValues.isInvoiced === undefined) {
      urlSearchParams.delete(SearchParamKeys.IsInvoiced);
    } else {
      urlSearchParams.set(
        SearchParamKeys.IsInvoiced,
        cachedFilterValues.isInvoiced.toString(),
      );
    }
  };

  const fetchBookingOrders = useCallback(
    async (_paginationParams?: PaginationParams | null) => {
      setLoadingBookingOrders(true);
      try {
        let response: AxiosResponse<
          ApiResponse<BookingOrder[], PaginationMeta>
        >;

        if (
          searchParams.get(SearchParamKeys.Category) ===
          BookingOrderFilterCategory.Urgent
        ) {
          response = await BookingOrderAPI.getIncompleteBookingOrders(
            incompleteBookingOrderApiFilter,
            _paginationParams,
          );
        } else {
          response = await BookingOrderAPI.getBookingOrders(
            bookingOrderApiFilter,
            _paginationParams,
          );
        }

        setBookingOrders(
          _paginationParams == null
            ? response.data.data
            : (prevState) => [...prevState].concat(response.data.data),
        );

        setPaginationMeta(response.data.meta);
        setPaginationParams({ page: response.data.meta.page + 1 });
      } finally {
        setLoadingBookingOrders(false);
      }
    },
    [bookingOrderApiFilter, incompleteBookingOrderApiFilter, searchParams],
  );

  const fetchOpenClassBookingOrders = useCallback(
    async (nextPaginationParams: PaginationParams = { page: 1 }) => {
      const page = nextPaginationParams.page ?? 1;
      setLoadingOpenClassBookingOrders(true);
      try {
        const { data } = await OpenClassAPI.getBookingOrderSummaries({
          from: asCalendarDate(bookingOrderApiFilter.from),
          to: asCalendarDate(bookingOrderApiFilter.to),
          subsidiaryCompanyIds: bookingOrderApiFilter.subsidiaryCompanyIds,
          courseIds: bookingOrderApiFilter.courseIds,
          contactPersonIds: getNumberListFromURLSearchParams(
            searchParams,
            SearchParamKeys.ContactPersonIds,
          ),
          page,
          pageSize: 100,
        });
        setOpenClassBookingOrders((current) =>
          [...(page > 1 ? current : []), ...data.data].sort((left, right) =>
            right.order.orderDate.localeCompare(left.order.orderDate),
          ),
        );
        setOpenClassPaginationMeta(data.paginationMeta);
        setOpenClassPaginationParams({ page: data.paginationMeta.page + 1 });
      } catch {
        message.error(t('views.OpenClasses.loadFailed'));
      } finally {
        setLoadingOpenClassBookingOrders(false);
      }
    },
    [bookingOrderApiFilter, searchParams, t],
  );

  useEffect(() => {
    if (!location.search) {
      return;
    }

    if (isOpenClassOrdersTab) {
      setBookingOrders([]);
      setPaginationParams({ page: 1 });
      setPaginationMeta(null);
      setOpenClassBookingOrders([]);
      setOpenClassPaginationParams({ page: 1 });
      setOpenClassPaginationMeta(null);
      fetchOpenClassBookingOrders({ page: 1 });
      return;
    }

    setOpenClassBookingOrders([]);
    setOpenClassPaginationParams({ page: 1 });
    setOpenClassPaginationMeta(null);
    setBookingOrders([]);
    fetchBookingOrders();
    setPaginationParams({ page: 1 });
    setPaginationMeta(null);
  }, [fetchBookingOrders, fetchOpenClassBookingOrders, isOpenClassOrdersTab]);

  /*
   * Debounce the call to fetchBookingsOrders to prevent the infinite scroll component
   * from calling it multiple times before the state has finished updating.
   */
  const debounceFetchBookingOrders = useMemo(
    () =>
      debounce(
        (
          _bookingOrderApiFilter?: BookingOrderApiFilter,
          _paginationParams?: PaginationParams | null,
        ) => fetchBookingOrders(_paginationParams),
        10,
      ),
    [fetchBookingOrders],
  );

  const onTabChange = (newTabKey: BookingOrderFilterCategory) => {
    setSearchParams((prev) => {
      const updatedSearchParams = new URLSearchParams(prev);
      updatedSearchParams.set(SearchParamKeys.Category, newTabKey);

      if (
        currentTab === BookingOrderFilterCategory.Urgent &&
        newTabKey !== BookingOrderFilterCategory.Urgent
      ) {
        setSearchParamsToCachedValues(updatedSearchParams);
      }

      switch (newTabKey) {
        case BookingOrderFilterCategory.All:
          updatedSearchParams.delete(SearchParamKeys.Statuses);
          break;
        case BookingOrderFilterCategory.OpenClassOrders:
          updatedSearchParams.delete(SearchParamKeys.Statuses);
          break;
        case BookingOrderFilterCategory.Urgent:
          setCachedFilterValues(
            getBookingOrderFilterFromSearchParams(updatedSearchParams),
          );
          updatedSearchParams.delete(SearchParamKeys.Statuses);
          updatedSearchParams.delete(SearchParamKeys.ClientCompanyIds);
          updatedSearchParams.delete(SearchParamKeys.InstructorIds);
          updatedSearchParams.delete(SearchParamKeys.CourseIds);
          updatedSearchParams.delete(SearchParamKeys.InvoiceRecipientIds);
          updatedSearchParams.delete(SearchParamKeys.IsInvoiced);
          updatedSearchParams.set(
            SearchParamKeys.From,
            dayjs().startOf('day').toISOString(),
          );
          updatedSearchParams.set(
            SearchParamKeys.To,
            dayjs().add(30, 'days').toISOString(),
          );
          break;
        default:
          updatedSearchParams.set('statuses', newTabKey);
      }

      setCurrentTab(newTabKey);
      return updatedSearchParams;
    });
  };

  const defaultSubsidiaryCompanyId = useMemo(() => {
    if (
      !currentUser ||
      !userHasPermission(currentUser, Permission.BOOKING_ORDER_READ)
    ) {
      return;
    }

    if (
      userHasPermission(currentUser, Permission.BOOKING_ORDER_READ_GLOBAL) &&
      currentUser.preferredSubsidiaryCompany
    ) {
      return currentUser.preferredSubsidiaryCompany.id;
    }

    if (currentUser.subsidiaryCompanies.length > 0) {
      return currentUser.subsidiaryCompanies
        .filter((company) => company.market.id === currentUser.market.id)
        .map((subsidiaryCompany) => subsidiaryCompany.id)[0];
    }

    return undefined;
  }, [currentUser]);

  useEffect(() => {
    // Set default values if search params are missing
    if (!location.search) {
      const updatedSearchParams = new URLSearchParams(searchParams);
      updatedSearchParams.set(
        SearchParamKeys.Category,
        BookingOrderFilterCategory.Ordered,
      );
      updatedSearchParams.set(
        SearchParamKeys.Statuses,
        BookingOrderStatus.Ordered,
      );

      if (defaultSubsidiaryCompanyId) {
        updatedSearchParams.set(
          SearchParamKeys.SubsidiaryCompanyIds,
          defaultSubsidiaryCompanyId.toString(),
        );
      }

      setSearchParams(updatedSearchParams);
    }
  }, [defaultSubsidiaryCompanyId, searchParams, setSearchParams]);

  return (
    <>
      <div className="flex flex-row items-end mt-2">
        <h2>{t('views.BookingOrders.orders')}</h2>
      </div>
      <BookingOrderFilterTabs
        defaultTab={currentTab}
        onTabChange={onTabChange}
      />
      <div className="flex flex-row">
        <div className="w-3/4">
          {isOpenClassOrdersTab ? (
            <InfiniteScroll
              hasMore={
                !loadingOpenClassBookingOrders &&
                (openClassPaginationMeta?.page ?? 1) <
                  (openClassPaginationMeta?.lastPage ?? 1)
              }
              loadMore={() => {
                if (!loadingOpenClassBookingOrders) {
                  fetchOpenClassBookingOrders(openClassPaginationParams);
                }
              }}>
              <OpenClassBookingOrderResults
                loading={loadingOpenClassBookingOrders}
                openClassBookingOrders={openClassBookingOrders}
              />
            </InfiniteScroll>
          ) : (
            <>
              <InfiniteScroll
                loadMore={() => {
                  if (!loadingBookingOrders) {
                    setLoadingBookingOrders(true);
                    return debounceFetchBookingOrders(bookingOrderApiFilter, {
                      ...paginationParams,
                      page: paginationParams?.page,
                    });
                  }
                }}
                hasMore={
                  !loadingBookingOrders &&
                  (paginationMeta?.page ?? 1) < (paginationMeta?.lastPage ?? 1)
                }>
                {bookingOrders?.map((bookingOrder) => (
                  <BookingOrderCard
                    key={bookingOrder.id}
                    bookingOrder={bookingOrder}
                  />
                ))}
              </InfiniteScroll>
              {bookingOrders.length === 0 && !loadingBookingOrders && (
                <Empty
                  className="pt-3"
                  description={<span>{t('views.BookingOrders.noOrders')}</span>}
                  image={Empty.PRESENTED_IMAGE_DEFAULT}
                />
              )}
              {loadingBookingOrders && (
                <div className="flex justify-center pt-10">
                  <Spin size="large" />
                </div>
              )}
            </>
          )}
        </div>
        {isOpenClassOrdersTab ? (
          <OpenClassBookingOrderFilterPicker
            className="w-1/4 pl-4"
            defaultSubsidiaryCompanyId={defaultSubsidiaryCompanyId}
          />
        ) : (
          <BookingOrderFilterPicker
            className="w-1/4 pl-4"
            defaultSubsidiaryCompanyId={defaultSubsidiaryCompanyId}
          />
        )}
      </div>
    </>
  );
};

export default BookingOrders;
